import axios from "axios";

const BASE_URL = `/api`;

//  Cấu hình Axios để dễ dàng tái sử dụng
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Thêm interceptor vào request
api.interceptors.request.use(
  (config) => {
    // Lấy token từ localStorage (hoặc Redux, Context API...)
    const token = JSON.parse(localStorage.getItem("token"))?.accessToken || "";

    // Nếu có token, thêm vào header Authorization
    const exceptRoutes = ["/auth/oauth-login", "order/getByIdList"];

    const isExcept = exceptRoutes.some((e) => {
      return config.url.includes(e);
    });

    if (token && !isExcept) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },

  (error) => {
    // Xử lý lỗi trước khi request được gửi đi
    return Promise.reject(error);
  },
);

// Interceptor cho response (Unpack BaseResponse một cách tương thích ngược)
api.interceptors.response.use(
  (response) => {
    const resData = response.data;

    if (
      resData &&
      typeof resData === "object" &&
      "code" in resData &&
      "message" in resData &&
      "data" in resData
    ) {
      const innerData = { ...resData };

      if (innerData !== null && innerData !== undefined) {
        if (typeof innerData === "object" || Array.isArray(innerData)) {
          // Định nghĩa thuộc tính 'data' trỏ về chính nó để tương thích ngược với các hàm gọi .data
          if (!Object.prototype.hasOwnProperty.call(innerData, "data")) {
            Object.defineProperty(innerData, "data", {
              get() {
                return this;
              },
              configurable: true,
              enumerable: false,
            });
          }
          // Định nghĩa thuộc tính 'code' và 'message'
          if (!Object.prototype.hasOwnProperty.call(innerData, "code")) {
            Object.defineProperty(innerData, "code", {
              value: resData.code,
              writable: true,
              configurable: true,
              enumerable: false,
            });
          }
          if (!Object.prototype.hasOwnProperty.call(innerData, "message")) {
            Object.defineProperty(innerData, "message", {
              value: resData.message,
              writable: true,
              configurable: true,
              enumerable: false,
            });
          }
          response.data = innerData.data;
          response.statusText = innerData.message;
          response.status = innerData.code;
        } else {
          // Đối với kiểu dữ liệu nguyên thủy (primitive), bọc bằng đối tượng tương ứng để gắn thuộc tính
          let wrappedObj;
          if (typeof innerData === "string") {
            // eslint-disable-next-line no-new-wrappers
            wrappedObj = new String(innerData);
          } else if (typeof innerData === "boolean") {
            // eslint-disable-next-line no-new-wrappers
            wrappedObj = new Boolean(innerData);
          } else if (typeof innerData === "number") {
            // eslint-disable-next-line no-new-wrappers
            wrappedObj = new Number(innerData);
          }

          if (wrappedObj) {
            Object.defineProperty(wrappedObj, "data", {
              get() {
                return this;
              },
              configurable: true,
              enumerable: false,
            });
            Object.defineProperty(wrappedObj, "code", {
              value: resData.code,
              writable: true,
              configurable: true,
              enumerable: false,
            });
            Object.defineProperty(wrappedObj, "message", {
              value: resData.message,
              writable: true,
              configurable: true,
              enumerable: false,
            });
            response.data = wrappedObj.data;
            response.statusText = wrappedObj.message;
            response.status = wrappedObj.code;
          }
        }
      }
    }
    return response;
  },
  (error) => {
    console.error("API Error:", error.status);
    if (error.status === 403) {
      // Chuyển hướng đến trang 403 khi nhận được lỗi 403 từ API
      window.location.href = "/403";
    }
    return Promise.reject(error);
  },
);

//  Function API cho từng module
//  API Category
export const customerAPI = {
  getAll: () => api.get("/customer"),
  getAllPaginated: (body) => api.post(`/customer/search`, body),
  getById: (id) => api.get(`/customer/${id}`),
  create: (data) => api.post("/customer", data),
  update: (id, data) => api.put(`/customer/${id}`, data),
  delete: (id) => api.delete(`/customer/${id}`),
  getSampleFile: () => api.get("/customer/sampleExcel"),
  importExcel: (data) =>
    api.post("/customer/importExcel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  importJson: (data) => api.post("/customer/importJson", data),
};

export const branchAPI = {
  getAll: () => api.get("/branch"),
  getAllPaginated: (body) => api.post(`/branch/search`, body),
  getById: (id) => api.get(`/branch/${id}`),
  create: (data) =>
    api.post("/branch", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, data) =>
    api.put(`/branch/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id) => api.delete(`/branch/${id}`),
  getByActiveFlag: () => api.get("/branch/active"),
  searchByName: (keyword) => api.get(`/branch/search?keyword=${keyword}`),
  getSampleFile: () => api.get("/branch/sampleExcel"),
  importExcel: (data) =>
    api.post("/branch/importExcel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  importJson: (data) => api.post("/branch/importJson", data),
};

//  API Category
export const categoryAPI = {
  getAll: () => api.get("/category"),
  getAllPaginated: (page, limit) =>
    api.get(`/category?page=${page}&limit=${limit}`),
  getById: (id) => api.get(`/category/${id}`),
  create: (data) => api.post("/category/add-category", data),
  update: (id, data) => api.put(`/category/${id}`, data),
  delete: (id) => api.delete(`/category/${id}`),
  getSampleFile: () => api.get("/category/sampleExcel"),
  importExcel: (data) =>
    api.post("/category/importExcel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  importJson: (data) => api.post("/category/importJson", data),
  searchCategories: (data) => api.post("/category/searchCategories", data),
};

//  API Product
export const productAPI = {
  getAll: () => api.get("/product"),
  getAllPaginated: (body) => api.post(`/product/search`, body),
  getById: (id) => api.get(`/product/${id}`),
  getStocks: (id) => api.get(`/product/${id}/stocks`),
  getStockCard: (id, branchId) =>
    api.get(`/product/${id}/stock-card`, {
      params: branchId ? { branchId } : {},
    }),
  create: (data) =>
    api.post("/product/add-product", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }), // Đảm bảo hỗ trợ upload file
  update: (id, data) =>
    api.put(`/product/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id) => api.delete(`/product/${id}`),
  importExcel: (data) =>
    api.post("/product/importExcel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  importJson: (data, branchId) =>
    api.post("/product/importJson", data, { params: { branchId } }),
  getSampleFile: () => api.get("/product/sampleExcel"),
};

export const orderAPI = {
  getAllPaginated: (body) => api.post(`/order/search`, body),
  searchPos: (body) => api.post(`/order/searchPos`, body),
  getById: (id) => api.get(`/order/${id}`),
  saveOrder: (orderData) => api.post("/order/createOrder", orderData),
  updateOrder: (orderData) => api.put("/order/updateOrder", orderData),
  updateOrderStatus: (orderId, newStatus) =>
    api.put(`/order/${orderId}/status`, { newStatus }),
  searchByOrderCode: (keyword) =>
    api.get("/order/searchByOrderCode", { params: { keyword } }),
  getByIdAndStatus: (customerId, orderId, status) =>
    api.get(`/order/${customerId}/${orderId}/${status}`),
  delete: (id) => api.put(`/order/delete/${id}`),
  getByIdList: (ids, token) =>
    api.get(`order/getByIdList?ids=${ids}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  getImportSampleFile: () => api.get("/order/sampleExcel"),
};

//  API User
export const userAPI = {
  getAll: () => api.get("/user"),
  search: (data) => api.post("/user/search", data),
  getById: (id) => api.get(`/user/${id}`),
  getByUsername: (username) => api.get(`/user/username/${username}`),
  update: (id, data) => api.put(`/user/${id}`, data),
  delete: (id) => api.delete(`/user/${id}`),
  getAllUserGoogle: () => api.get("/user/getAllUsersGG"),
  changePassword: (id, newPassword) =>
    api.put(`/user/${id}/change-password`, { newPassword }),
};

// API menu
export const menuAPI = {
  search: (data) => api.post("/menu/search", data),
  getAll: () => api.get("/menu"),
  getById: (id) => api.get(`/menu/${id}`),
  create: (data) => api.post("/menu", data),
  update: (id, data) => api.put(`/menu/${id}`, data),
  delete: (id) => api.delete(`/menu/${id}`),
  importExcel: (data) =>
    api.post("/menu/importExcel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  getSampleFile: () => api.get("/menu/sampleExcel"),
  getMenuByUser: () => api.get("/menu/getMenuByUser"),
};

//API permissions
export const roleAPI = {
  search: (data) => api.post("/role/search", data),
  getAllRoles: () => api.get("/role/"),
  getById: (id) => api.get(`/role/${id}`),
  getByName: (name) => api.get(`/role/name/${name}`),
  create: (data) => api.post("/role/", data),
  update: (id, data) => api.put(`/role/${id}`, data),
  delete: (id) => api.delete(`/role/${id}`),
  assignMenus: (roleId, menuIds) =>
    api.post(`/role/${roleId}/assign-menus`, menuIds),
  getRoleMenus: (roleId) => api.get(`/role/${roleId}/menus`),
};

// API Auth
export const authAPI = {
  login: (data, token) => api.post("/auth/login", data),
  loginOauth: (token) =>
    api.post(
      "/auth/oauth-login",
      {},
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    ),
  register: (data) => api.post("/auth/register", data),
  registerOauth: (token) =>
    api.post(
      "/auth/oauth-register",
      {},
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    ),
};

export const posAPI = {
  checkout: (data) => api.post("/pos/checkout", data),
  searchProducts: (params) => api.get("/pos/products", { params }),
  getTodayOrders: (branchId) =>
    api.get("/pos/orders/today", { params: { branchId } }),
  getRevenue: (date) => api.get("/pos/revenue", { params: { date } }),
};

// API Supplier
export const supplierAPI = {
  getAll: () => api.get("/supplier/getAllSuppliers"),
  getById: (id) => api.get(`/supplier/${id}`),
  getAllPaginated: (body) => api.post(`/supplier/searchSupplier`, body),
  add: (supplierData) => api.post("/supplier/add-supplier", supplierData),
  update: (id, supplierData) => api.put(`/supplier/${id}`, supplierData),
  delete: (id) => api.delete(`/supplier/${id}`),
  getSampleFile: () => api.get("/supplier/sampleExcel"),
  importExcel: (data) =>
    api.post("/supplier/importExcel", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  importJson: (data) => api.post("/supplier/importJson", data),
};

// API Purchase Order
export const purchaseOrderAPI = {
  addNew: (data) => api.post("/purchase-order/createPurchaseOrder", data),
  update: (data) => api.put("/purchase-order/updatePurchaseOrder", data),
  search: (data) => api.post("/purchase-order/searchPurchaseOrder", data),
  deleteById: (id) => api.delete(`/purchase-order/deleteById/${id}`),
  getById: (id) => api.get(`/purchase-order/getById/${id}`),
  getImportSampleFile: (brancId) =>
    api.get(`/purchase-order/sampleExcel/${brancId}`),
};

// API Purchase Return
export const purchaseReturnAPI = {
  addNew: (data) => api.post("/purchase-return/createPurchaseReturn", data),
  update: (data) => api.put("/purchase-return/updatePurchaseReturn", data),
  search: (data) => api.post("/purchase-return/searchPurchaseReturn", data),
  deleteById: (id) => api.delete(`/purchase-return/deleteById/${id}`),
  getById: (id) => api.get(`/purchase-return/getById/${id}`),
  getImportSampleFile: () => api.get("/purchase-return/sampleExcel"),
};

// API Sales Return
export const salesReturnAPI = {
  addNew: (data) => api.post("/sales-return/createSalesReturn", data),
  update: (data) => api.put("/sales-return/updateSalesReturn", data),
  search: (data) => api.post("/sales-return/searchSalesReturn", data),
  deleteById: (id) => api.delete(`/sales-return/deleteById/${id}`),
  getById: (id) => api.get(`/sales-return/getById/${id}`),
  getImportSampleFile: () => api.get("/sales-return/sampleExcel"),
};

// API Area
export const areaAPI = {
  getAll: () => api.get("/area/list"),
  getByBranch: (branchId) => api.get(`/area/getByBranch/${branchId}`),
  createOrUpdate: (branchId, data) =>
    api.post(`/area/createOrUpdate/${branchId}`, data),
  deleteMulti: (id) => api.delete(`/area/delete?ids=${id}`),
};

// API Dashboard
export const dashboardAPI = {
  getTotalBussiness: () => api.get("/statistic/getTotalBussiness"),
  getTotalOrderStatus: () => api.get("/statistic/getTotalOrderStatus"),
  getTotalRevenue: (filterType) =>
    api.get(`/statistic/getTotalRevenue/${filterType}`),
};

// API Báo cáo tài chính
export const reportAPI = {
  // Báo cáo doanh thu theo khoảng thời gian
  getRevenueSummary: (params) =>
    api.get("/statistic/revenue-summary", { params }),
  // Báo cáo nhập hàng theo khoảng thời gian
  getPurchaseSummary: (params) =>
    api.get("/statistic/purchase-summary", { params }),
  // Báo cáo lợi nhuận
  getProfitLoss: (params) => api.get("/statistic/profit-loss", { params }),
  // Top sản phẩm bán chạy
  getTopProducts: (params) => api.get("/statistic/top-products", { params }),
  // Doanh thu theo khách hàng
  getRevenueByCustomer: (params) =>
    api.get("/statistic/revenue-by-customer", { params }),
  // Tổng hợp báo cáo tài chính đầy đủ (cho export Excel)
  getFullFinancialReport: (params) =>
    api.get("/statistic/financial-report", { params }),
  // Lấy dữ liệu đơn hàng theo khoảng thời gian
  getOrdersByDateRange: (params) =>
    api.post("/order/search", {
      searchKey: "",
      status: -1,
      sortBy: "id",
      sortType: "desc",
      pageNum: 0,
      pageSize: 1000,
      ...params,
    }),
  // Lấy dữ liệu nhập kho theo khoảng thời gian
  getPurchaseByDateRange: (params) =>
    api.post("/purchase-order/searchPurchaseOrder", {
      searchKey: "",
      status: -1,
      sortBy: "id",
      sortType: "desc",
      pageNum: 0,
      pageSize: 1000,
      ...params,
    }),
};

// API vị trí bản đồ
export const geographyAPI = {
  getAll: (params) => api.get("/geography/all", { params }),
  search: (params) => api.get("/geography/search", { params }),
};

// ─── API Kiểm Kho ────────────────────────────────────────────────────────────
export const inventoryCheckAPI = {
  // Tạo phiếu kiểm kho mới
  create: (data) => api.post("/inventory-check", data),

  // Tìm kiếm & phân trang danh sách phiếu
  search: (body) => api.post("/inventory-check/search", body),

  // Chi tiết 1 phiếu
  getById: (id) => api.get(`/inventory-check/${id}`),

  // Danh sách sản phẩm trong phiếu
  getItems: (checkId) => api.get(`/inventory-check/${checkId}/items`),

  // Nhập số lượng thực tế cho 1 sản phẩm
  updateActual: (checkId, productId, actual, note = "") => {
    let url = `/inventory-check/${checkId}/items/${productId}`;
    const params = [];
    if (actual !== null && actual !== undefined) {
      params.push(`actual=${actual}`);
    }
    if (note) {
      params.push(`note=${encodeURIComponent(note)}`);
    }
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
    return api.put(url);
  },

  // Xóa sản phẩm khỏi phiếu kiểm kho
  deleteItem: (checkId, productId) =>
    api.delete(`/inventory-check/${checkId}/items/${productId}`),

  // Danh sách sản phẩm có chênh lệch
  getDiscrepancies: (checkId) =>
    api.get(`/inventory-check/${checkId}/discrepancies`),

  // Nộp phiếu chờ duyệt (IN_PROGRESS → PENDING)
  submit: (checkId, comment = "") =>
    api.post(`/inventory-check/${checkId}/submit`, { comment }),

  // Phê duyệt phiếu (PENDING → COMPLETED)
  approve: (checkId, comment = "") =>
    api.post(`/inventory-check/${checkId}/approve`, { comment }),

  // Từ chối phiếu (PENDING → IN_PROGRESS)
  reject: (checkId, comment = "") =>
    api.post(`/inventory-check/${checkId}/reject`, { comment }),

  // Lịch sử phê duyệt
  getLogs: (checkId) => api.get(`/inventory-check/${checkId}/logs`),

  // Xuất báo cáo Excel
  exportExcel: (checkId) =>
    api
      .get(`/inventory-check/${checkId}/export`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `kiem-kho-${checkId}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }),
};

// ─── API Công Nợ ─────────────────────────────────────────────────────────────
export const debtAPI = {
  // Tìm kiếm + phân trang
  search: (data) => api.post("/debt/search", data),
  // Danh sách tổng hợp công nợ nhà cung cấp (còn nợ)
  getSupplierDebts: () => api.get("/debt/supplier"),
  // Danh sách tổng hợp công nợ khách hàng (còn nợ)
  getCustomerDebts: () => api.get("/debt/customer"),
  // Chi tiết 1 khoản nợ
  getById: (id) => api.get(`/debt/${id}`),
  // Lịch sử thanh toán
  getPayments: (id) => api.get(`/debt/${id}/payments`),
  // Ghi nhận 1 lần thanh toán
  addPayment: (id, data) => api.post(`/debt/${id}/payment`, data),
  // Tổng hợp theo đối tác
  getSummaryByPartner: (debtType, partnerId) =>
    api.get(`/debt/summary/${debtType}/${partnerId}`),
  // Xóa mềm công nợ
  deleteDebt: (id) => api.delete(`/debt/${id}`),
};

// ─── API Sổ Quỹ ───────────────────────────────────────────────────────────────
export const cashBookAPI = {
  // Tìm kiếm + phân trang
  search: (data) => api.post("/cash-book/search", data),
  // Tạo phiếu thu/chi thủ công
  create: (data) => api.post("/cash-book", data),
  // Cập nhật phiếu
  update: (id, data) => api.put(`/cash-book/${id}`, data),
  // Hủy phiếu
  cancel: (id) => api.delete(`/cash-book/${id}`),
  // Chi tiết
  getById: (id) => api.get(`/cash-book/${id}`),
  // Số dư quỹ
  getBalance: (branchId) =>
    api.get("/cash-book/balance", { params: { branchId } }),
  // Tổng hợp thu/chi theo khoảng thời gian
  getSummary: (params) => api.get("/cash-book/summary", { params }),
};

export default api;
